import { TFunction } from 'i18next';
import customConfig from 'src/config';

import { Region } from '../../../types';
import { setNestObjByKeyArr } from '../utils/fns';
import {
  DlpPolicyFilters,
  PolicyFilters,
  PolicyHolderTypeEnum,
  SpamLevelEnum,
  Targets,
} from '../utils/types';
import cloneDeep from 'lodash/cloneDeep';

import { TargetType } from '@Utils/enums';
import { isInUIC } from '@Utils/common';
import { IS_UAE } from '@Utils/const';

const envSettings = window as any;
const config = {
  policyDescription: '',
  policyName: '',
  policyTarget: {
    targetType: 'user',
    targetGroups: [],
    targets: [],
    empty: true,
  },
  policyHolderType: PolicyHolderTypeEnum.ATP, //1: ATP, 2: DLP
  policyLang: 'en', // en by default
  policyRmsEnabled: 0,
  isSkipFolderEnabled: 0,
  policySubType: 0,
  policyType: 1,
  holderType: 1,
  boxAdvancedFeatureEnabled: false,
  isSoEnabled: false,
  isEncryptionEnabled: false,
  policyPriority: 1,
  policyRealtimeEnabled: 0,
  policyMode: 0,
  tmFileblockings: {
    fbActionType: 10,
    fbCompressedFileScanningType: 0,
    fbDecompressLayerLimit: 5,
    fbEnabled: 0,
    fbExceptionFileExtensionEnabled: 0,
    fbExceptionFileExtensionBlocked: 0,
    fbExceptionFileNameBlocked: 0,
    fbExceptionFileNameEnabled: 0,
    fbExceptionFileTypeEnabled: 0,
    fbBlockFileExtensionBlocked: 0,
    fbBlockFileExtensionEnabled: 0,
    fbBlockFileNameBlocked: 0,
    fbBlockFileNameEnabled: 0,
    fbBlockFileTypeEnabled: 0,
    fbExtractFileCountLimit: 9999,
    fbExtractFileRatioLimit: 99,
    fbExtractFileSizeLimit: 31457280,
    fbFileExtensionBlocked: 0,
    fbFileNameBlocked: 0,
    fbFileNameEnabled: 0,
    fbFileTypeEnabled: 0,
    fbNotifyType: 0,
    fbReplacementFileName:
      'A_POLICY_VIOLATED_FILE_WAS_DETECTED_AND_REMOVED.TXT',
    fbReplacementText:
      'Cloud App Security detected and removed a file named "[Attachment Name]" that violated file blocking policy from the original email entity. You can safely save or delete this replacement attachment.',
    fbScanningType: 1,
    fbTransportEnabled: 0,
    fbApprovedSenderEmailEnabled: 0,
    fbApprovedSenderEmailEntries: '',
    fbApplyBound: 7,
    fbSecondActionEnabled: 0,
    fbSecondActionType: 6,
    fbExceptionFileTypeList: '',
    fbExceptionFileExtensionList: '',
    fbExceptionFileNameList: '',
    fbBlockFileExtensionList: '',
    fbBlockFileTypeList: '',
    fbBlockFileNameList: '',
    fbAdvancedIncomingRuleEnabled: 0,
    fbIncomingRuleBlockFileTypeEnabled: 0,
    fbIncomingRuleBlockFileTypeList: '',
    fbIncomingRuleBlockFileExtensionEnabled: 0,
    fbIncomingRuleBlockFileExtensionList: '',
    fbIncomingRuleBlockFileNameEnabled: 0,
    fbIncomingRuleBlockFileNameList: '',
    notification: {
      ntfAdminIntervalNum: 2,
      ntfAdminIntervalType: 0,
      ntfAdminMessage:
        'Cloud App Security detected an attachment that meets File Blocking policies.\r\n\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tAttached files: %attachments%',
      ntfAdminNotifySettingType: 0,
      ntfAdminOccurenceNum: 50,
      ntfAdminSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection',
      ntfEnableEndUserRestore: 0,
      ntfDoNotNotifyExternalUser: 0,
      ntfEnableNotifyAdmin: 1,
      ntfEnableNotifyRecipient: 0,
      ntfInternalRecipientMessage:
        'Cloud App Security detected an attachment that meets File Blocking policies, and took action on it to comply with the information security policies of the organization.\r\n\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tAttached files: %attachments%\r\n\r\n',
      ntfInternalRecipientSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection',
      ntfToAccount: '',
      ntfRecipientGroupId: 0,
      ntfRecipientGroupEnabled: 1,
    },
  },
  tmVirusscans: {
    vsActionType: 0,
    vsActiveActionNotifyType: 0,
    vsAdditionalThreats: 3,
    vsAdditionalThreatsActionType: 109,
    vsAdditionalThreatsNotifyType: 1,
    vsAllThreatsActionType: 2,
    vsAllThreatsNotifyType: 1,
    vsBodysizeMaximum: 30,
    vsCompressedFilecountMaximum: 9999,
    vsCompressedFilelayerMaximum: 5,
    vsCompressedFilesizeMaximum: 100,
    vsCompressedRatioMaximum: 1000,
    vsDefaultScanningType: 0,
    vsDetectedThreatType: 1,
    vsEnableDonotCleanCompressedFile: 0,
    vsEnableHeuristicScanOnMacro: 1,
    vsEnableIntellitrap: 1,
    vsEnableMacroStripping: 0,
    vsEnableMassingMailing: 0,
    vsEnableScanFileExt: 1,
    vsEnablePredictiveMachineLearningFeedback: 1,
    vsEnablePredictiveMachineLearning: 1,
    vsEnableScanMessagebody: 1,
    vsEnableVirusscan: 1,
    vsEncryptedFileActionType: -1,
    vsEncryptedCompressedFileActionType: 6,
    vsOtherEncryptedFileActionType: 6,
    vsEncryptedFileNotifyType: -1,
    vsEncryptedCompressedFileNotifyType: 0,
    vsOtherEncryptedFileNotifyType: 0,
    vsFilesizeMaximum: 30,
    vsGenericsActionType: 6,
    vsGenericsNotifyType: 1,
    vsHeuristicScanActionLevel: 2,
    vsHeuristicScanActionType: 0,
    vsRansomwareActionType: 109,
    vsRansomwareNotifyType: 1,
    vsMaliciousActionType: 109,
    vsMaliciousNotifyType: 1,
    vsMassingMailingActionType: 103,
    vsMassingMailingNotifyType: 0,
    vsNeedToCheckAttachmentSize: 1,
    vsNeedToCheckBodySize: 1,
    vsNeedToCheckComprFileLayer: 1,
    vsNeedToCheckComprFileRatio: 1,
    vsNeedToCheckDecomFileCount: 1,
    vsNeedToCheckDecomFileSize: 1,
    vsNeedToCheckEntityCount: 1,
    vsNeedToCheckOleLayer: 1,
    vsOleLayerMaximum: 3,
    vsOverRestrictionActionType: 6,
    vsOverRestrictionNotifyType: 0,
    vsPackersActionType: 109,
    vsPackersNotifyType: 1,
    vsReplacementFileName: 'VIRUS_DETECTED_AND_REMOVED.TXT',
    vsReplacementFileNameUnscannable:
      'UNSCANNABLE_MESSAGE_PARTS_DETECTED_AND_REMOVED.TXT',
    vsReplacementText:
      'Cloud App Security detected and removed a security risk from the original email message [Attachment Name].',
    vsReplacementTextUnscannable:
      'Cloud App Security deleted the attached file which is encrypted and password protected or matches scan restriction criteria from the original email attachment "[Attachment Name]".',
    vsSpecifiedFileExtObjects:
      '""|arj|bat|bin|boo|cab|chm|cla|class|com|csc|dll|doc|dot|drv|eml|exe|gz|hlp|hta|htm|html|htt|ini|jar|jpeg|jpg|js|jse|lnk|lzh|mdb|mpd|mpp|mpt|msg|mso|nws|ocx|oft|ovl|pdf|php|pif|pl|pot|pps|ppt|prc|rar|reg|rtf|scr|shs|sys|tar|vbe|vbs|vsd|vss|vst|vxd|wml|wsf|xla|xls|xlt|xml|z|zip|{*|',
    vsTextTagSubjectContent: 'Suspicious File:',
    vsSpecifiedFileTypeObjects: '',
    vsUncleanableThreatsActionType: 109,
    vsVirusesActionType: 109,
    vsVirusesNotifyType: 1,
    vsWormsTrojansActionType: 10,
    vsWormsTrojansNotifyType: 1,
    vsOverFilesizeRestrictionActionType: 6,
    vsOverFilesizeRestrictionNotifyType: 0,
    vsOverDecompressRestrictionActionType: 6,
    vsOverDecompressRestrictionNotifyType: 0,
    vsApplyBound: 7,
    vsDisarmEnabled: 0,
    vsDisarmActionType: 2,
    vsDisarmNotifyType: 0,
    vsDisarmExceptEnabled: 0,
    vsDisarmExceptSenderString: '',
    vsDisarmExceptReceiverString: '',
    vsNonOfficeDisarmEnabled: 0,
    vsSecondActionEnabled: 0,
    vsSecondActionType: 6,
    notification: {
      ntfAdminIntervalNum: 2,
      ntfAdminIntervalType: 0,
      ntfAdminMessage:
        'Cloud App Security detected an email message with %security_risk_name% characteristics.\r\n\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tDetected by: %detected_by%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tAttached files: %attachments%',
      ntfAdminNotifySettingType: 0,
      ntfAdminOccurenceNum: 50,
      ntfAdminSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection',
      ntfEnableEndUserRestore: 0,
      ntfDoNotNotifyExternalUser: 0,
      ntfEnableNotifyAdmin: 1,
      ntfEnableNotifyRecipient: 0,
      ntfInternalRecipientMessage:
        'Cloud App Security detected an email message with %security_risk_name% characteristics in your mailbox, and took action on it to comply with the information security policies of the organization.\r\n\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tDetected by: %detected_by%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tAttached files: %attachments%\r\n\r\n',
      ntfInternalRecipientSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection',
      ntfToAccount: '',
      ntfRecipientGroupId: 0,
      ntfRecipientGroupEnabled: 1,
    },
  },
  tmWebthreatprotections: {
    wtpActionType: 109,
    wtpActionBlockedUrl: 109,
    wtpApprovedSenderEmailEnabled: 0,
    wtpApprovedSenderEmailEntries: '',
    wtpApprovedUrlListEnabled: 0,
    wtpApprovedUrlListEntries: '',
    wtpBlockedUrlListEnabled: 0,
    wtpBlockedUrlListEntries: '',
    wtpEnabled: 1,
    wtpSecuritylevelEnabled: 0,
    wtpUrlcategoryEnabled: 0,
    wtpUrlcategoryEntries: '',
    wtpExtractAttachmentUrl: 0,
    wtpNotifyType: 0,
    wtpBlockedUrlNotifyType: 0,
    wtpSecurityLevel: 1,
    wtpSuspiciousTag: 'Suspicious URL: ',
    wtpSuspiciousTagBlockedUrl: 'Suspicious URL: ',
    wtpUnratedAction: 0,
    wtpReplacementText:
      'Cloud App Security detected and removed a file named "[Attachment Name]" that violated file blocking policy from the original email entity. You can safely save or delete this replacement attachment.',
    wtpApplyBound: 1,
    wtpInternalDomainToApproveUrl: 1,
    wtpDynamicScanEnabled: 1,
    wtpApprovedHeaderEnabled: 0,
    wtpApprovedHeaderList: '',
    wtpRetroScanEnabled: 0,
    wtpUrlcvEnabled: 0,
    wtpTimeOfClickEnabled: 0,
    wtpTimeOfClickApplyType: 0,
    wtpFileTagEnabled: 0,
    wtpFileAction: 251,
    wtpFileActionNotifyType: 0,
    wtpSecondActionEnabled: 0,
    wtpSecondActionType: 6,
    notification: {
      ntfAdminIntervalNum: 2,
      ntfAdminIntervalType: 0,
      ntfAdminMessage:
        'Cloud App Security detected an email message with a suspicious URL.\r\n\r\nURL: %suspicious_url%\r\nRisk level: %risk_level%\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tDetected by: %detected_by%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tCategory: %url_category%',
      ntfAdminNotifySettingType: 0,
      ntfAdminOccurenceNum: 50,
      ntfAdminSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection',
      ntfEnableEndUserRestore: 0,
      ntfDoNotNotifyExternalUser: 0,
      ntfEnableNotifyAdmin: 1,
      ntfEnableNotifyRecipient: 0,
      ntfInternalRecipientMessage:
        'Cloud App Security detected an email message with a suspicious URL, and took action on it to comply with the information security policies of the organization.\r\n\r\nURL: %suspicious_url%\r\nRisk level: %risk_level%\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tDetected by: %detected_by%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tCategory: %url_category%\r\n\r\n',
      ntfInternalRecipientSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection',
      ntfToAccount: '',
      ntfRecipientGroupId: 0,
      ntfRecipientGroupEnabled: 1,
    },
  },
  tmDdafilters: {
    ddaActionHighRisk: 109,
    ddaActionLowRisk: 6,
    ddaActionMediumRisk: 109,
    ddaApplyActionUnscannable: 0,
    ddaApplyActionUnscannableEnabled: 0,
    ddaApplyDiffActionKnownThread: 0,
    ddaApplyDiffActionKnownThreadEnabled: 0,
    ddaEnabled: 1,
    ddaFileEnabled: 1,
    ddaURLEnabled: 0,
    ddaQuarantine: 0,
    ddaSuspiciousTagHighRisk: 'High Risk: ',
    ddaSuspiciousTagMediumRisk: 'Medium Risk: ',
    ddaSuspiciousTagLowRisk: 'Low Risk: ',
    ddaActionUnknowRisk: 6,
    ddaSuspiciousTagUnknowRisk: 'Unknown Risk: ',
    ddaActionUnknowRiskEnabled: 1,
    ddaHighRiskNotifyType: 1,
    ddaMediumRiskNotifyType: 1,
    ddaLowRiskNotifyType: 0,
    ddaUnknowRiskNotifyType: 0,
    ddaReplacementText:
      'Cloud App Security detected and removed a file named "[Attachment Name]" that violated file blocking policy from the original email entity. You can safely save or delete this replacement attachment.',
    ddaApplyBound: 1,
    ddaApprovedSenderEmailEnabled: 0,
    ddaMonitorModeEnabled: 0,
    ddaFileTempQuarantineEnabled: 0,
    ddaURLTempQuarantineEnabled: 0,
    ddaApprovedSenderEmailEntries: '',
    ddaApprovedFileNameEnabled: 0,
    ddaApprovedFileNameEntries: '',
    notification: {
      ntfAdminIntervalNum: 2,
      ntfAdminIntervalType: 0,
      ntfAdminMessage:
        'Cloud App Security detected an email message with suspicious characteristics during sandbox analysis.\r\n\r\nRisk level: %risk_level%\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tAttached files: %attachments%\r\n\r\n',
      ntfAdminNotifySettingType: 0,
      ntfAdminOccurenceNum: 50,
      ntfAdminSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection (Virtual Analyzer)',
      ntfEnableEndUserRestore: 0,
      ntfDoNotNotifyExternalUser: 0,
      ntfEnableNotifyAdmin: 1,
      ntfEnableNotifyRecipient: 0,
      ntfInternalRecipientMessage:
        'Cloud App Security detected an email message with suspicious characteristics during sandbox analysis, and took action on it to comply with the information security policies of the organization.\r\n\r\nRisk level: %risk_level%\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tAttached files: %attachments%\r\n\r\n',
      ntfInternalRecipientSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection (Virtual Analyzer)',
      ntfToAccount: '',
      ntfRecipientGroupId: 0,
      ntfRecipientGroupEnabled: 1,
    },
  },
  tmAntispamfilters: {
    asActionBec: 109,
    asActionOthers: 207,
    asActionPhish: 109,
    asActionRansome: 109,
    asGreyMailEnabled: 1,
    asActionMalicious: 109,
    asActionBlockedSender: 109,
    asActionGrayEmail: 6,
    asActionScam: 109,
    wsNotifyRecipientActionType: 5,
    asApprovedSenderEmailEnabled: 0,
    asApprovedSenderEmailEntries: '',
    asBlockedSenderEmailEnabled: 0,
    asBlockedSenderEmailEntries: '',
    asBecNotifyType: 0,
    asApplyBound: 7,
    asSkipInternalSpamDetection: 1,
    asEnabled: Number(envSettings.DEPLOY_POLICY_DEFAULT_ANTISPAM_ENABLED) || 0,
    wsEnabled: 0,
    asOthersNotifyType: 0,
    asPhishNotifyType: 0,
    asRansomeNotifyType: 0,
    asMaliciousNotifyType: 0,
    asBlockedSenderNotifyType: 0,
    asGrayEmailNotifyType: 0,
    asScamNotifyType: 0,
    asGrayEmailMmlEnabled: 1,
    asGrayEmailSocialNetworkEnabled: 1,
    asGrayEmailForumEnabled: 1,
    asGrayEmailBulkEnabled: 1,
    asNewbornDetectionEnabled: 0,
    wsNotifyExpectedSenderEnabled: 1,
    wsNotifyExpectedSenderAttachOriginMailEnabled: 1,
    wsNotifyExpectedSenderWithFeedbackEnabled: 1,
    ntfWritingStyleToExpectedSenderFeedbackMessage:
      'Did this message actually come from you?\r\n\r\n <!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="%writing_style_feedback_yes%" style="height:30px;v-text-anchor:middle;width:60px;" arcsize="5%" strokecolor="#7BC4F5" fillcolor="#7BC4F5"><w:anchorlock/><center style="color:#ffffff;font-family:Helvetica, Arial,sans-serif;font-size:16px;">Yes</center></v:roundrect><![endif]--><a href="%writing_style_feedback_yes%" style="background-color:#7BC4F5;border:1px solid #7BC4F5;border-radius:3px;color:#ffffff;display:inline-block;font-family:sans-serif;font-size:14px;line-height:30px;text-align:center;text-decoration:none;width:60px;-webkit-text-size-adjust:none;mso-hide:all;">Yes</a>  <!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="%writing_style_feedback_no%" style="height:30px;v-text-anchor:middle;width:60px;" arcsize="5%" strokecolor="#7BC4F5" fillcolor="#7BC4F5"><w:anchorlock/><center style="color:#ffffff;font-family:Helvetica, Arial,sans-serif;font-size:16px;">No</center></v:roundrect><![endif]--><a href="%writing_style_feedback_no%" style="background-color:#7BC4F5;border:1px solid #7BC4F5;border-radius:3px;color:#ffffff;display:inline-block;font-family:sans-serif;font-size:14px;line-height:30px;text-align:center;text-decoration:none;width:60px;-webkit-text-size-adjust:none;mso-hide:all;">No</a></div>',
    wsNotifyAdministratorEnabled: 1,
    wsNotifyAdministratorAttachOriginMailEnabled: 0,
    ntfWritingStyleExpectedSenderSubject: 'Did you write this message?',
    ntfWritingStyleExpectedSenderMessage:
      '<div><font style="font-size:13px;font-weight:700;">Hello %expected_sender_displayname%,</font>\r\n\r\nThe email message below does not seem like something you would usually write, so please indicate if you sent it. You can review the attachment for the complete text if the original email message is attached. Your response will help improve the filtering system.\r\n\r\n<div><table><tr><td style="padding-left: 50px;"><font color="#DBD9D1">%date% %time%</font>\r\n<font color="#DBD9D1" size="6px">%sender%</font>\r\n%subject%\r\n<font color="#DBD9D1">To: %recipient%</font>\r\n%origin_mail_content%</td></tr></table></div>\r\n',
    ntfWritingStyleToAdminAccount: '',
    ntfWritingStyleAdministratorSubject:
      'Trend Micro Cloud App Security Advanced Threat Protection (Writing Style Analysis Violation)',
    ntfWritingStyleAdministratorMessage:
      "An email message received from outside the corporate network did not match the supposed sender's writing style.\r\n\r\nSpam category: %spam_category%\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tAttached files: %attachments%\r\nSupposed sender: %expected_sender%\r\nMessage id: %origin_mail_message_id%\r\n",
    asSpamLevel:
      Number(envSettings.DEPLOY_POLICY_DEFAULT_ANTISPAM_RISKLEVEL) ||
      SpamLevelEnum.high,
    asSnapLevel: 2,
    asSuspiciousTagBec: 'Spam: ',
    asSuspiciousTagOthers: 'Spam: ',
    asSuspiciousTagPhish: 'Spam: ',
    asSuspiciousTagRansome: 'Spam: ',
    asSuspiciousTagMalicious: 'Spam: ',
    asSuspiciousTagBlockedSender: 'Spam: ',
    wsSuspiciousTag: 'Probable BEC attack: ',
    asSuspiciousTagGrayEmail: 'Spam: ',
    asSuspiciousTagScam: 'Spam: ',
    wsDisclaimer:
      'WARNING: Do NOT reply to this email message.\r\nThe writing style of the content below appears to be different from how %expected_sender_displayname% usually writes. Please use a different email address or make a telephone call to verify if %expected_sender_displayname% has actually sent this email message. If not, then forward this email message to your administrator immediately.',
    asEnabledFeedback: 1,
    asApprovedHeaderEnabled: 0,
    asApprovedHeaderList: '',
    asBlockedHeaderEnabled: 0,
    asBlockedHeaderRuleList: '',
    asSpoofDetectEnabled: 0,
    asRetroScanEnabled: 0,
    asAggressiveHpuEnabled: 0,
    asUnusualSignalEnabled: 0,
    asSuspiciousTagSuspiciousSender: 'Suspicious Sender:  ',
    asActionSuspiciousSender: 6,
    asSuspiciousSenderNotifyType: 0,
    asAggressiveNameSpoofingEnabled: 0,
    asDisclaimerGrayEmail:
      'WARNING: This email appears to be graymail. DO NOT respond to this email. Report the email to your administrator if necessary.',
    asDisclaimerScam:
      'WARNING: This email appears to be scam. DO NOT respond to this email. Report the email to your administrator if necessary.',
    asDisclaimerBec:
      'WARNING: This email appears to be BEC. DO NOT respond to this email. Report the email to your administrator if necessary.',
    asDisclaimerPhish:
      'WARNING: This email appears to be phishing. DO NOT respond to this email. Report the email to your administrator if necessary.',
    asDisclaimerRansome:
      'WARNING: This email appears to be ransomware. DO NOT respond to this email. Report the email to your administrator if necessary.',
    asDisclaimerMalicious:
      'WARNING: This email appears to be malicious spam. DO NOT respond to this email. Report the email to your administrator if necessary.',
    asDisclaimerOtherSpam:
      'WARNING: This email appears to be other spam. DO NOT respond to this email. Report the email to your administrator if necessary.',
    asDisclaimerSuspiciousSender:
      'WARNING: This email appears to be suspicious sender. DO NOT respond to this email. Report the email to your administrator if necessary.',
    notification: {
      ntfAdminIntervalNum: 2,
      ntfAdminIntervalType: 0,
      ntfAdminMessage:
        'Cloud App Security detected a spam email message.\r\n\r\nSpam category: %spam_category%\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tDetected by: %detected_by%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tAttached files: %attachments%\r\n\r\n',
      ntfAdminNotifySettingType: 0,
      ntfAdminOccurenceNum: 50,
      ntfAdminSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection (Advanced Spam Protection)',
      ntfEnableEndUserRestore: 0,
      ntfDoNotNotifyExternalUser: 0,
      ntfEnableNotifyAdmin: 1,
      ntfEnableNotifyRecipient: 0,
      ntfInternalRecipientMessage:
        'Cloud App Security detected a spam email message, and took action on it to comply with the information security policies of the organization.\r\n\r\nSpam category: %spam_category%\r\nAction taken: %action%\r\nMessage details:\r\n\tReceived: %date% %time%\r\n\tAffected User: %violator%\r\n\tFound in: %foundin%\r\n\tTriggered policy: %policy_name%\r\n\tDetected by: %detected_by%\r\n\tSender: %sender%\r\n\tRecipient: %recipient%\r\n\tSubject: %subject%\r\n\tAttached files: %attachments%\r\n\r\n',
      ntfInternalRecipientSubject:
        'Trend Micro Cloud App Security Advanced Threat Protection (Advanced Spam Protection)',
      ntfToAccount: '',
      ntfRecipientGroupId: 0,
      ntfRecipientGroupEnabled: 1,
    },
  },
};

const i18nBasicReplaceKeys = [
  'policy.policyTemplate.tmAntispamfilters.asDisclaimerBec',
  'policy.policyTemplate.tmAntispamfilters.asDisclaimerGrayEmail',
  'policy.policyTemplate.tmAntispamfilters.asDisclaimerMalicious',
  'policy.policyTemplate.tmAntispamfilters.asDisclaimerOtherSpam',
  'policy.policyTemplate.tmAntispamfilters.asDisclaimerPhish',
  'policy.policyTemplate.tmAntispamfilters.asDisclaimerRansome',
  'policy.policyTemplate.tmAntispamfilters.asDisclaimerScam',
  'policy.policyTemplate.tmAntispamfilters.asDisclaimerSuspiciousSender',
  'policy.policyTemplate.tmAntispamfilters.asSuspiciousTagBec',
  'policy.policyTemplate.tmAntispamfilters.asSuspiciousTagBlockedSender',
  'policy.policyTemplate.tmAntispamfilters.asSuspiciousTagGrayEmail',
  'policy.policyTemplate.tmAntispamfilters.asSuspiciousTagMalicious',
  'policy.policyTemplate.tmAntispamfilters.asSuspiciousTagOthers',
  'policy.policyTemplate.tmAntispamfilters.asSuspiciousTagPhish',
  'policy.policyTemplate.tmAntispamfilters.asSuspiciousTagRansome',
  'policy.policyTemplate.tmAntispamfilters.asSuspiciousTagScam',
  'policy.policyTemplate.tmAntispamfilters.asSuspiciousTagSuspiciousSender',
  'policy.policyTemplate.tmAntispamfilters.ntfWritingStyleAdministratorMessage',
  'policy.policyTemplate.tmAntispamfilters.ntfWritingStyleAdministratorSubject',
  'policy.policyTemplate.tmAntispamfilters.ntfWritingStyleExpectedSenderMessage',
  'policy.policyTemplate.tmAntispamfilters.ntfWritingStyleExpectedSenderSubject',
  'policy.policyTemplate.tmAntispamfilters.ntfWritingStyleToExpectedSenderFeedbackMessage',
  'policy.policyTemplate.tmAntispamfilters.wsDisclaimer',
  'policy.policyTemplate.tmAntispamfilters.wsSuspiciousTag',
  // 'policy.policyTemplate.tmAntispamfilters.wsSuspiciousTag.ntfWritingStyleAdministratorMessage',
  // 'policy.policyTemplate.tmAntispamfilters.wsSuspiciousTag.ntfWritingStyleAdministratorSubject',
  // 'policy.policyTemplate.tmAntispamfilters.wsSuspiciousTag.ntfWritingStyleExpectedSenderMessage',
  // 'policy.policyTemplate.tmAntispamfilters.wsSuspiciousTag.ntfWritingStyleExpectedSenderSubject',
  'policy.policyTemplate.tmDdafilters.ddaReplacementText',
  'policy.policyTemplate.tmDdafilters.ddaSuspiciousTagHighRisk',
  'policy.policyTemplate.tmDdafilters.ddaSuspiciousTagLowRisk',
  'policy.policyTemplate.tmDdafilters.ddaSuspiciousTagMediumRisk',
  'policy.policyTemplate.tmDdafilters.ddaSuspiciousTagUnknowRisk',
  'policy.policyTemplate.tmDdafilters.notification.ntfToAccount',
  'policy.policyTemplate.tmDpfilters.dpReplacementText',
  'policy.policyTemplate.tmFileblockings.fbReplacementFileName',
  'policy.policyTemplate.tmFileblockings.fbReplacementText',
  'policy.policyTemplate.tmFileblockings.notification.ntfToAccount',
  'policy.policyTemplate.tmKeywordfilters.kfReplacementText',
  'policy.policyTemplate.tmVirusscans.notification.ntfToAccount',
  'policy.policyTemplate.tmVirusscans.vsReplacementFileName',
  'policy.policyTemplate.tmVirusscans.vsReplacementFileNameUnscannable',
  'policy.policyTemplate.tmVirusscans.vsReplacementText',
  'policy.policyTemplate.tmVirusscans.vsReplacementTextUnscannable',
  'policy.policyTemplate.tmVirusscans.vsTextTagSubjectContent',
  'policy.policyTemplate.tmWebthreatprotections.notification.ntfToAccount',
  'policy.policyTemplate.tmWebthreatprotections.wtpReplacementText',
  'policy.policyTemplate.tmWebthreatprotections.wtpSuspiciousTag',
  'policy.policyTemplate.tmWebthreatprotections.wtpSuspiciousTagBlockedUrl',
  // "policy.policyTemplate.SF.tmWebthreatprotections.notification.ntfAdminMessage",
  // "policy.policyTemplate.SF.tmWebthreatprotections.notification.ntfAdminSubject",
  // "policy.policyTemplate.SF.tmWebthreatprotections.notification.ntfInternalRecipientMessage",
  // "policy.policyTemplate.SF.tmWebthreatprotections.notification.ntfInternalRecipientSubject",
  // "policy.policyTemplate.SF.tmVirusscans.notification.ntfAdminMessage",
  // "policy.policyTemplate.SF.tmVirusscans.notification.ntfAdminSubject",
  // "policy.policyTemplate.SF.tmVirusscans.notification.ntfInternalRecipientMessage",
  // "policy.policyTemplate.SF.tmVirusscans.notification.ntfInternalRecipientSubject",
  // "policy.policyTemplate.MTC.tmFileblockings.notification.ntfAdminMessage",
  // "policy.policyTemplate.MTC.tmFileblockings.notification.ntfAdminSubject",
  // "policy.policyTemplate.MTC.tmVirusscans.notification.ntfAdminMessage",
  // "policy.policyTemplate.MTC.tmVirusscans.notification.ntfAdminSubject",
  // "policy.policyTemplate.MTC.tmWebthreatprotections.notification.ntfAdminMessage",
  // "policy.policyTemplate.MTC.tmWebthreatprotections.notification.ntfAdminSubject",
  // "policy.policyTemplate.MTC.tmDdafilters.notification.ntfAdminMessage",
  // "policy.policyTemplate.MTC.tmDdafilters.notification.ntfAdminSubject",
  // "policy.policyTemplate.SF.tmFileblockings.notification.ntfInternalRecipientMessage",
  // "policy.policyTemplate.SF.tmFileblockings.notification.ntfInternalRecipientSubject",
  // "policy.policyTemplate.SF.tmFileblockings.notification.ntfAdminSubject",
  // "policy.policyTemplate.SF.tmFileblockings.notification.ntfAdminMessage",
];

export function getDefaultAtpConfig() {
  return cloneDeep(config);
}

export function getTargetProperty(
  policyString: string,
  translateIndex: number
) {
  const policyArr = policyString.split('.');
  const thirdString = policyArr[translateIndex];
  if (
    [
      ...Object.values(PolicyFilters),
      ...Object.values(DlpPolicyFilters),
    ].includes(thirdString as PolicyFilters | DlpPolicyFilters)
  ) {
    return policyString.split('.').slice(translateIndex).join('.');
  }
  return null;
}

function whiteLabelingStringReplace(config: any) {
  [
    'tmFileblockings',
    'tmVirusscans',
    'tmWebthreatprotections',
    'tmDdafilters',
    'tmAntispamfilters',
  ].forEach((key) => {
    if (config[key] && config[key].notification) {
      for (const item in config[key].notification) {
        if (typeof config[key].notification[item] === 'string') {
          config[key].notification[item] = config[key].notification[item]
            .replace(
              /Trend Micro/g,
              customConfig.ui.feature.whiteLabeling.firstName
            )
            .replace(
              /Cloud App Security/g,
              customConfig.ui.feature.whiteLabeling.lastName
            );
        }
      }
    }
  });
}

export function policyNotificationProductNameReplace(
  config: any,
  productName: string,
  replaceProductName: string
) {
  const regexPattern = new RegExp(productName, 'gm');
  config = JSON.parse(
    JSON.stringify(config).replace(regexPattern, replaceProductName)
  );
  return config;
}

function defaultAccountReplace(config: any, accountEmail: string) {
  if (
    config.tmAntispamfilters &&
    typeof config.tmAntispamfilters.ntfWritingStyleToAdminAccount !==
      'undefined'
  ) {
    config.tmAntispamfilters.ntfWritingStyleToAdminAccount = accountEmail;
  }
  [
    'tmFileblockings',
    'tmVirusscans',
    'tmWebthreatprotections',
    'tmDdafilters',
    'tmAntispamfilters',
    'tmRiskAiFilters',
  ].forEach((key) => {
    if (config[key] && config[key].notification) {
      // console.log('config[key].notification.ntfToAccount', key, accountEmail);
      config[key].notification.ntfToAccount = accountEmail;
    }
  });
}

export function getTemplateConfig(
  t: TFunction<'translation'>,
  config: any,
  accountEmail: string | undefined,
  coverI18nKeys: string[],
  ntfI18nKeys: string[]
) {
  try {
    i18nBasicReplaceKeys.forEach((key) => {
      const targetProperty = getTargetProperty(key, 2);
      if (targetProperty !== null) {
        const propArr: string[] = targetProperty?.split('.');
        setNestObjByKeyArr(config, propArr, t(key));
      }
    });

    coverI18nKeys.forEach((key) => {
      const targetProperty = getTargetProperty(key, 3);
      if (targetProperty !== null) {
        const propArr: string[] = targetProperty?.split('.');
        setNestObjByKeyArr(config, propArr, t(key));
      }
    });

    //ntf key always have service name,for example :"policy.policyTemplate.EX.tmDdafilters.notification.ntfAdminSubject",
    ntfI18nKeys.forEach((key) => {
      const targetProperty = getTargetProperty(key, 3);
      if (targetProperty !== null) {
        const propArr: string[] = targetProperty?.split('.');
        setNestObjByKeyArr(config, propArr, t(key));
      }
    });

    if (
      customConfig.ui.feature.whiteLabeling.enabled &&
      window.DEPLOY_REGION === Region.UAE
    ) {
      whiteLabelingStringReplace(config);
    }
    if (isInUIC()) {
      config = policyNotificationProductNameReplace(
        config,
        'Cloud App Security',
        'Cloud Email and Collaboration Protection'
      );
    } else if (IS_UAE) {
      config = policyNotificationProductNameReplace(
        config,
        'Trend Micro',
        'Cyber Eye'
      );
      config = policyNotificationProductNameReplace(
        config,
        'Cloud App Security',
        'Cloud Service Security'
      );
    }

    // replace default account
    if (accountEmail) {
      defaultAccountReplace(config, accountEmail);
    }
  } catch (e) {
    console.log(e);
  }

  return config;
}

const emptyTargetUserGroup: Targets = {
  targets: [],
  targetGroups: [],
  targetType: TargetType.UserAndGroup,
  empty: true,
};

export const getEmptyTargetUserGroup = () => {
  return cloneDeep(emptyTargetUserGroup);
};
